
import { mapActions, mapGetters, mapState, mapMutations } from 'vuex';
import _debounce from 'lodash/debounce';
import CoolLightBox from 'vue-cool-lightbox';
import {
  generateYoutubePreview,
  generateYoutubeVideoSourcePath,
  generateRutubePreview,
  generateRutubeVideoSourcePath,
} from '@/plugins/helpers/file';
import { generateProductUrl, generateCategoryUrl } from '@/plugins/helpers/catalog';
import { dateFormat, weekdayFormatShort, phraseDeclension } from '@/plugins/formatting';
import { productGroupTypes } from '~/plugins/enums/product';
import { receiveMethods } from '@/plugins/enums/checkout';
import ProductSliderImages from '@/components/catalog/productSliderImages';
import SpecList from '@/components/catalog/specList';
import ScrollUpBtn from '@/components/elements/ScrollUpBtn';
import ProductBundle from '@/components/catalog/productBundle';
import Reviews from '@/components/catalog/reviews';
import Button from '@/components/elements/Button';
import OrderList from '@/components/catalog/orderList';
import CustomRate from '~/components/elements/CustomRate.vue';
import env from '@/.env.json';
import 'vue-cool-lightbox/dist/vue-cool-lightbox.min.css';
import CustomImage from '~/components/elements/CustomImage';
import CustomYandexShare from '~/components/elements/CustomYandexShare';
import VideoIcon from '~/components/elements/VideoIcon';
import HintBlock from '~/components/elements/HintBlock.vue';
import PreOrderModal from '~/components/catalog/product/PreOrderModal.vue';

export default {
  name: 'Product',
  components: {
    HintBlock,
    VideoIcon,
    CustomYandexShare,
    CustomImage,
    ProductSliderImages,
    SpecList,
    ProductBundle,
    Button,
    CustomRate,
    OrderList,
    CoolLightBox,
    Reviews,
    ScrollUpBtn,
    PreOrderModal,
  },
  props: {
    product: {
      type: Object,
      required: true,
    },
    bundles: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      deliveries: [],
      optionImage: null,
      loadingDelivery: true,
      count: 0,
      wordVariantsBonus: ['бонус', 'бонусов', 'бонуса'],
      wordVariantsParameters: ['вариант', 'вариантов', 'варианта'],
      receiveMethods,
      checkingRR: null,
      rrID: env.RETAIL_ROCKET_MAIN_ID,
      shareEnabled: env.PRODUCT_SHARE_ENABLED,
      rrLoaded: false,
      scrolledMainInfo: false,
      imageOpenIndex: null,
      hasListDeliveries: env.DELIVERY_PRODUCT_CARD_LOAD || false,
      preOrderModal: false,
    };
  },
  computed: {
    ...mapGetters('currency', { getCurrency: 'getCurrency' }),
    ...mapGetters('product', [
      'getPrice',
      'getOldPrice',
      'getCharacteristicValue',
      'getCategories',
      'getOptionsCharacteristics',
      'getNextCombination',
      'getCharacteristics',
    ]),
    ...mapGetters('geolocation', ['getSelectedCity', 'getSelectedCityType']),
    ...mapGetters('cart', ['productInCart', 'getProductCount', 'getProductCountWithBundle']),
    ...mapGetters('favorites', ['productInFavorites']),
    ...mapGetters('auth', { showVAT: 'showVAT' }),
    ...mapState('geolocation', ['selectedCity']),
    ...mapState('auth', ['hasSession']),
    ...mapState('route', {
      categoryCode: (state) => state.params.category,
      productCode: (state) => state.params.product,
    }),
    getShareSrc() {
      const media = this.product.media;

      if (Array.isArray(media) && media.length > 0) {
        return media[0].url.small;
      }

      return '';
    },
    actualPriceValueWithoutVAT() {
      return this.getCurrency(this.product?.priceBeforeVat);
    },
    vatValue() {
      return this.getCurrency(this.product?.vatValue);
    },
    isProductDescription() {
      return this.product?.description?.content;
    },
    isProductIngredient() {
      return this.product?.ingredients?.length;
    },
    brandUrl() {
      const { brand } = this.product;

      return generateCategoryUrl(productGroupTypes.BRANDS, brand?.code);
    },
    isMax() {
      return (
        this.getProductCount(this.product.id) >= this.product.stock.qty ||
        this.getProductCountWithBundle(this.product.id) >= this.product.stock.qty
      );
    },
    mqIsMobile() {
      return this.$mq === 'mobileXs';
    },
    badgeList() {
      const badges = [...this.product?.badges];
      if (this.product.oldPrice) {
        const difference = this.product.oldPrice.value - this.product.price.value;
        badges.push(`Экономия ${difference}`);
      }
      return badges;
    },
    isFew() {
      return this.product.stock.qty <= 5 && this.product.stock.qty > 1;
    },
    isLast() {
      return this.product.stock.qty === 1;
    },
    isFavorites() {
      return this.productInFavorites(this.product.productId);
    },
    textFavorite() {
      return this.isFavorites ? 'В избранном' : 'В избранное';
    },
    pricePanelVisible() {
      return this.mqIsMobile || this.scrolledMainInfo;
    },
    productVideo() {
      return this.product?.description?.video || '';
    },
    productVideoOrigin() {
      return this.product?.description?.origin || '';
    },
    pngImages() {
      let slides = [];

      slides = this.product.media.map(image => {
        return {
          url: image.url,
          type: 'image',
        };
      });

      if (this.productVideo) {
        const videoSrc = this.productVideoOrigin === 'rutube'
          ? generateRutubePreview(this.productVideo)
          : generateYoutubePreview(this.productVideo);

        slides.push({
          src: videoSrc,
          type: 'video',
        });
      }


      return slides;
    },
    isChatAvailable() {
      return !!env.LIVETEX_ID
    },
    galleryItems() {
      let slides = [];

      slides = this.product?.media.map(image => {
        return {
          src: image.url?.large || '',
        };
      });

      if (this.productVideo) {
        const videoSrc = this.productVideoOrigin === 'rutube'
          ? generateRutubeVideoSourcePath(this.productVideo)
          : generateYoutubeVideoSourcePath(this.productVideo);

        slides.push({
          src: videoSrc,
          mediaType: this.productVideoOrigin === 'rutube' ? 'iframe' : null,
        });
      }

      return slides;
    },
  },
  watch: {
    selectedCity() {
      this.getDelivery();
    },
    rrLoaded(value) {
      if (value) {
        window.retailrocket.markup.render();
        clearInterval(this.checkingRR);
      }
    },
  },
  created() {
    this.count = this.getProductCount(this.product.id);
    this.debounce_onChangeCount = _debounce(this.changeCount, 300);
  },
  mounted() {
    if (this.selectedCity) {
      this.getDelivery();
    }

    this.$nextTick(() => {
      window.addEventListener('scroll', this.onScroll);
      this.onScroll();

      this.checkingRR = setInterval(() => {
        this.rrLoaded = !!window.retailrocket;
      }, 1000);
    });

    this.ymetricaEmitProductDetail(this.product);
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.onScroll);
  },
  methods: {
    ...mapActions({
      addToCartProduct: 'cart/addToCartProduct',
      changeCartProductItemCount: 'cart/changeCartProductItemCount',
      addFavoritesItem: 'favorites/addFavoritesItem',
      deleteFavoritesItem: 'favorites/deleteFavoritesItem',
      ymetricaEmitProductDetail: 'YMetrica/emitProductDetail',
      ymetricaEmitProductAddToCart: 'YMetrica/emitProductAddToCart',
    }),
    ...mapMutations({
      setVisibleLocationModal: 'geolocation/SET_VISIBLE_LOCATION_MODAL',
      setIsOpenedAuthModal: 'auth/SET_IS_OPENED_AUTH_MODAL',
    }),
    phraseDeclension,
    dateFormat,
    weekdayFormatShort,
    onScroll() {
      const element = this.$refs.addToCart;
      if (element && !this.mqIsMobile) {
        const elementPositionTop = element.getBoundingClientRect().bottom;
        this.scrolledMainInfo = elementPositionTop <= 0;
      } else {
        this.scrolledMainInfo = false;
      }
    },
    scrollToReviews() {
      const offset = 180;
      const positionTop = this.$refs.reviews.offsetTop - offset;
      this.scrollTo({
        top: positionTop,
        behavior: 'smooth',
      });
    },
    innerStyle(color) {
      return {
        backgroundColor: `#${color}`,
      };
    },
    onSelectOption(charCode, optValue) {
      const { code } = this.getNextCombination(charCode, optValue);
      this.$router.push({
        path: generateProductUrl(this.categoryCode, code),
      });
    },
    onAddCartButtonClick() {
      try {
        // eslint-disable-next-line no-undef
        rrApi.addToBasket(this.product.id);
      } finally {
        this.count = 1;
        this.addToCartProduct(this.product);
        this.ymetricaEmitProductAddToCart({product: this.product, quantity: this.count});
      }
    },
    async getDelivery() {
      if (this.hasListDeliveries) {
        try {
          this.loadingDelivery = true;
          const result = await this.$api.product.fetchProductDeliveries({ code: this.productCode });
          this.deliveries = result.methods;
          this.loadingDelivery = false;
        } catch (error) {
          console.error(error);
        }
      } else {
        this.loadingDelivery = false;
      }
    },
    onChangeCount() {
      this.count++;
      this.debounce_onChangeCount();
    },
    changeCount() {
      this.changeCartProductItemCount({
        product: this.product,
        count: this.count,
      });
    },
    openLocationModal() {
      this.setVisibleLocationModal(true);
    },
    deliveryTitle(id) {
      switch (id) {
        case this.receiveMethods.DELIVERY:
          return 'Доставка курьером';
        case this.receiveMethods.PICKUP:
          return 'Из пунктов выдачи';
        default:
          return '';
      }
    },
    scrollToDescription() {
      const offset = 24;
      const offsetTop = this.$refs?.description?.offsetTop || this.$refs?.characteristics?.offsetTop || 0;

      offsetTop && this.scrollTo({
        top: offsetTop - offset,
        behavior: 'smooth',
      });
    },
    scrollTo(options) {
      window.scrollTo(options);
    },
    openImage(index) {
      this.imageOpenIndex = index;
    },
    toggleToFavoritesItem() {
      if (!this.hasSession) {
        this.setIsOpenedAuthModal(true);
        return;
      }

      if (this.isFavorites) {
        this.deleteFavoritesItem(this.product.productId);
      } else {
        this.addFavoritesItem(this.product.productId);
      }
    },
    showChat() {
      window.LiveTex?.showActiveWindow();
    },
    showPreOrder() {
      if (!this.hasSession) {
        this.setIsOpenedAuthModal(true);
        return;
      }

      this.preOrderModal = true;
    },
  },
};
